import { useDispatch } from "react-redux"
import { useEffect, useMemo, useRef } from "react"
import {
    isLastFetchedItem,
    getPrevItemId,
    getNextItemId,
    RemoteData
} from "../Models/Client/RemoteData"
import { push } from "connected-react-router"
import { NavigationCursor, getCursor } from "../Models/Client/NavigationCursor"

export interface Navigation {
    onPrev?: () => void
    onNext?: () => void
    cursor: NavigationCursor
}

/**
 * This hook provides functionality for users to navigate previous/next item of a given `RemoteData`.
 */
export function useNavigation<M, P, K>(
    data: RemoteData<M, P, K> | undefined,
    buildUrl: (id: K) => string,
    onLoadMore?: () => void,
    currentID?: K
): Navigation | undefined {
    const dispatch = useDispatch()
    const navigationRef = useRef<Navigation | undefined>(undefined)
    const navigation = useMemo<Navigation | undefined>(() => {
        if (data === undefined || currentID === undefined) {
            return undefined
        }

        const cursor = getCursor(data, currentID)

        if (cursor === undefined) {
            return undefined
        }

        const prevID = getPrevItemId(data, currentID)
        const nextID = getNextItemId(data, currentID)

        return {
            cursor,
            onPrev: prevID && (() => dispatch(push(buildUrl(prevID)))),
            onNext: nextID && (() => dispatch(push(buildUrl(nextID))))
        }
    }, [dispatch, currentID, buildUrl, data])

    useEffect(() => {
        if (
            data !== undefined &&
            isLastFetchedItem(data, currentID) &&
            onLoadMore !== undefined
        ) {
            onLoadMore()
        }
    }, [onLoadMore, data, currentID])

    useEffect(() => {
        if (navigation !== undefined) {
            navigationRef.current = navigation
        }
    }, [navigation])

    // We return previous value ( `navigationRef.current` ) as fallback if `currentID` is not contained in `data`.
    // In this way, navigation is still available even if `data` gets updated and `currentID` is no longer included in `data`.
    // This is important for reviewing jobs to work.
    return navigation === undefined &&
        navigationRef.current !== undefined &&
        data !== undefined &&
        currentID !== undefined
        ? navigationRef.current
        : navigation
}
